import { BackgroundImage } from "@/components/Primitives/BackgroundImage"
import dynamic from 'next/dynamic';
import { getReadableImageUrl } from "@/lib/directus";
import { sanitizeHTML } from "@/lib/utils";
const AnimatedSection = dynamic(() => import('../Animations/useAnimations'), { ssr: false });
const ScreenAwareContent = dynamic(() => import('../../utils/screenAwareContent'), { 
  ssr: false,
  loading: () => <div className="min-h-screen h-screen" /> // Simple loading state
});

interface DirectusInterfaceData {
  heading?: {
    text?: string;
    title?: string;
  };
  text?: {
    text?: string;
  };
  image?: {
    alt: string;
    image_file: {
      image: {
        id: string;
        filename_download: string;
      };
    };
  };
  buttons?: {
    menu_items_id: {
      href: string;
      label: string;
    };
  }[];
}

export const fields = [
  // headings
  "heading.text",
  "heading.title",

  // text
  "text.text",

  // images
  "image.alt",
  "image.image_file.image.id",
  "image.image_file.image.filename_download",

  // buttons
  "buttons.menu_items_id.href",
  "buttons.menu_items_id.label",
] as const;

interface FacilitiesProps {
  zIndex: number;
  thresholds: number[][];
  transitionTime?: number;
  mobileThresholds: number[][];
  helperRotation?: number;
  data?: DirectusInterfaceData;
} 

export default function Facilities({ zIndex, thresholds, transitionTime, mobileThresholds, helperRotation, data }: FacilitiesProps) {  
  const imageId = data?.image?.image_file?.image?.id || "";
  const imageFilename = data?.image?.image_file?.image?.filename_download || "";
  
  return (
    <ScreenAwareContent
      supportedContent={
        <AnimatedSection
          helperRotation={helperRotation}
          className="h-screen overflow-hidden fixed w-full pt-16 xl:pt-32 xl:bg-[#d0dbec]  top-0 left-0"
          thresholds={[
            thresholds?.[0]?.[0],
            thresholds?.[thresholds.length - 1]?.[1],
          ]}
          mobileThresholds={[
            mobileThresholds?.[0]?.[0],
            mobileThresholds?.[mobileThresholds.length - 1]?.[1],
          ]}
          zIndex={zIndex}
          withZIndex={true}
          transitionTime={transitionTime}
          variants={{
            desktop: {
              visible: { y: 0, x: 0, opacity: 1 },
              above: { y: "100vh", x: 0, opacity: 1 },
              below: { y: "-100vh", x: 0, opacity: 1 }
            },
            mobile: {
              visible: { x: 0, y: 0, opacity: 1 },
              above: { x: "-100vw", y: 0, opacity: 1 },
              below: { x: 0, y: "-100vh", opacity: 1 }
            }
          }}
        > 
          <div className="h-full overflow-hidden relative w-full flex items-start lg:items-center">
          {/* Container */}
          <div className="gl-container flex flex-col lg:flex-row items-start lg:items-center lg:justify-between gl-desktop-scale">
            {/* Image Section */}
            <AnimatedSection 
            section="div"
            zIndex={zIndex} 
            thresholds={[
              thresholds?.[0]?.[0],
              thresholds?.[thresholds.length - 1]?.[1],
            ]} 
            mobileThresholds={[
              mobileThresholds?.[0]?.[0],
              mobileThresholds?.[0]?.[1],
            ]}
            className="relative w-full lg:w-[calc(66.666667%+20vw)] lg:-ml-[20vw]" 
            transitionTime={transitionTime}
            variants={{
              desktop: {
                visible: { opacity: 1, x: 0, y: 0, scale: 1 },
                above: { opacity: 1, x: "-100vw", y: 0, scale: 1 },
                below: { opacity: 1, x: 0, y: 0, scale: 1 },
              },
              mobile: {
                visible: { opacity: 1, x: 0, y: '30vh', scale: 4.5 },
                above: { opacity: 1, x: 0, y: '30vh', scale: 4.5 },
                below: { opacity: 1, x: 0, y: 0, scale: 1 },
              }
            }}  
            >
              <div className="relative h-48 w-full rounded-2xl overflow-hidden lg:h-[670px] lg:w-full lg:rounded-tr-full lg:rounded-br-full pl-16">
                <BackgroundImage
                  assetId={imageId}
                  temp={imageId ? getReadableImageUrl(imageId, imageFilename) : "/images/12.jpg"}
                  className="object-cover"
                  priority
                  quality={100}
                />
              </div>
            </AnimatedSection>

            {/* Content Section */}
            <div className="mt-6 lg:mt-0 lg:w-1/3 lg:pl-10 gl-desktop-scale">
              <h2 className="text-[2rem] font-essonnes font-light leading-tight md:text-5xl lg:text-[3.375rem] text-primary mb-6 lg:mb-16">
                {data?.heading?.title}
              </h2>
              
              <div 
                className="text-[1rem] font-normal leading-normal lg:text-[1.25rem] text-primary font-greycliff tracking-tight mb-6 lg:mb-20 [&>*]:text-[1rem] [&>*]:font-normal [&>*]:leading-normal lg:[&>*]:text-[1.25rem]"
                dangerouslySetInnerHTML={sanitizeHTML(data?.text?.text || "")}
              />

              {/* Buttons */}
              <div className="flex flex-col lg:flex-row gap-4 lg:gap-8">
                {data?.buttons?.map((button, index) => (
                  index === 0 ? (
                    <a 
                      key={index} 
                      href={button.menu_items_id.href || "#"} 
                      className="px-12 py-2.5 lg:px-6 lg:py-3.5 bg-primary rounded-3xl inline-block text-center"
                    >
                      <span className="text-white text-xl font-normal lg:font-medium font-greycliff leading-7 tracking-tight">
                        {button.menu_items_id.label}
                      </span>
                    </a>
                  ) : (
                    <a 
                      key={index} 
                      href={button.menu_items_id.href || "#"} 
                      className="hidden lg:flex items-center border-b border-primary pb-0.5 pr-7"
                    >
                      <span className="text-primary text-xl font-medium font-greycliff leading-7 tracking-tight">
                        {button.menu_items_id.label}
                      </span>
                    </a>
                  )
                ))}
              </div>
            </div>
          </div>
          </div>
        </AnimatedSection>
      }
      fallbackContent={
        <section className="overflow-hidden w-full lg:py-8 xl:bg-[#d0dbec]"> 
          <div className="h-full overflow-hidden relative w-full flex items-start lg:items-center">
          {/* Container */}
          <div className="gl-container flex flex-col xl:flex-row items-start xl:items-center xl:justify-between gl-desktop-scale">
            {/* Image Section */}
            <div className="relative w-full xl:w-[calc(66.666667%+20vw)] xl:-ml-[20vw]">
              <div className="relative h-48 w-full rounded-2xl overflow-hidden xl:h-[670px] xl:w-full xl:rounded-tr-full xl:rounded-br-full pl-16">
                <BackgroundImage
                  assetId={imageId}
                  temp={imageId ? getReadableImageUrl(imageId, imageFilename) : "/images/12.jpg"}
                  className="object-cover"
                  priority
                  quality={100}
                />
              </div>
            </div>

            {/* Content Section */}
            <div className="mt-6 xl:mt-0 xl:w-1/3 xl:pl-10 gl-desktop-scale">
              <h2 className="text-[2rem] font-essonnes font-light leading-tight md:text-5xl lg:text-[3.375rem] text-primary mb-6 xl:mb-16">
                {data?.heading?.title}
              </h2>
              
              <div 
                className="text-[1rem] font-normal leading-normal xl:text-[1.25rem] text-primary font-greycliff tracking-tight mb-6 xl:mb-20 [&>*]:text-[1rem] [&>*]:font-normal [&>*]:leading-normal xl:[&>*]:text-[1.25rem]"
                dangerouslySetInnerHTML={sanitizeHTML(data?.text?.text || "")}
              />

              {/* Buttons */}
              <div className="flex flex-col xl:flex-row gap-4 xl:gap-8">
                {data?.buttons?.map((button, index) => (
                  index === 0 ? (
                    <a 
                      key={index} 
                      href={button.menu_items_id.href || "#"} 
                      className="px-12 py-2.5 xl:px-6 xl:py-3.5 bg-primary rounded-3xl inline-block text-center"
                    >
                      <span className="text-white text-xl font-normal xl:font-medium font-greycliff leading-7 tracking-tight">
                        {button.menu_items_id.label}
                      </span>
                    </a>
                  ) : (
                    <a 
                      key={index} 
                      href={button.menu_items_id.href || "#"} 
                      className="hidden xl:flex items-center border-b border-primary pb-0.5 pr-7"
                    >
                      <span className="text-primary text-xl font-medium font-greycliff leading-7 tracking-tight">
                        {button.menu_items_id.label}
                      </span>
                    </a>
                  )
                ))}
              </div>
            </div>
          </div>
          </div>
        </section>
      }
    />
  );
}